import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import Input from '../../components/Input';
import { InputF } from '../../components/Input';
import { InputOption } from '../../components/Input';
import Env from '../../hooks/Env';
import { libelleEts, domaines, postesPer, type_postes } from '../RecrutementPer/config';
import './UpdateRecrutement.css';
import  { Alert2 } from '../../components/Alerte';
import ErrorPage from '../../components/Error';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const UpdateRecrutement = () => {
    useDocumentTitle('Mise à jour du recrutement');
    const { uid } = useParams();
    const navigate = useNavigate();
    const API_URL = Env().API_URL;
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [dataForm, setDataForm] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState({});
    const [additionalFiles, setAdditionalFiles] = useState([]); // Pour les fichiers complémentaires
    const [visibleFields, setVisibleFields] = useState({}); // Pour gérer l'affichage des champs optionnels
    const [valid, SetIsValid] = useState({
        isValidPhoneNumber: true,
    });
    const [selectedPostes, setSelectedPostes] = useState([]); // Pour gérer les postes sélectionnés en mode édition
    const [etablissementCounter, setEtablissementCounter] = useState(0); // Compteur global pour les établissements
    const [editingPostesMode, setEditingPostesMode] = useState(false); // true quand on charge les postes existants pour édition
    const [backendPostes, setBackendPostes] = useState([]); // postes originels venant du backend
    const maxFileSize = 1024 * 1024 * 2; // 2Mo
    const [statusError, setStatusError] = useState(null);
    // Récupérer les données du candidat
    useEffect(() => {
        const fetchCandidatData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API_URL}/recrutement/uid/${uid}`);
                
                if (response.status === 200) {
                    setDataForm(response.data);
                    // enregistrer la liste des postes fournis par le backend (postes non-nuls)
                    const _backendPostes = [];
                    Object.keys(response.data || {}).forEach(k => {
                        const m = k.match(/^poste(\d+)$/);
                        if (m && response.data[k]) {
                            _backendPostes.push(response.data[k]);
                        }
                    });
                    setBackendPostes(_backendPostes);
                    
                    // Initialiser les champs visibles en fonction des données existantes
                    const fieldsToCheck = [
                        'diplome3', 'diplome4', 'diplome5', 'diplome6',
                        'publication2', 'publication3', 'publication4', 'publication5', 
                        'publication6', 'publication7', 'publication8', 'publication9' ,
                        'communication2', 'communication3', 'communication4', 'communication5',
                        'justificatif1', 'justificatif2', 'justificatif3', 'justificatif4',
                        'justificatif5', 'justificatif6', 'justificatif7', 'justificatif8'
                    ];
                    
                    const initialVisibility = {};
                    fieldsToCheck.forEach(field => {
                        initialVisibility[field] = !!response.data[field]; // true si le fichier existe
                    });
                    setVisibleFields(initialVisibility);
                    
                    setLoading(false);
                } else {
                    setError("Impossible de récupérer les données du candidat");
                    setLoading(false);
                }
            } catch (err) {
                console.error('Erreur lors de la récupération des données:', err);
                setStatusError(err?.response?.status || 500);
                setError("Une erreur s'est produite lors de la récupération des données");
                setLoading(false);
            }
        };

        if (uid) {
            fetchCandidatData();
        }
    }, [uid, API_URL]);

    const handleChange = (e) => {
        const name = e.target.name;
        let value = e.target.value;
        const type = e.target.type;
        const checked = e.target.checked;

        // Gestion des checkboxes
        if (type === 'checkbox') {
            if (name.startsWith('etablissement')) {
                setDataForm(prevData => ({
                    ...prevData,
                    [name]: checked ? value : null
                }));
                return;
            } else {
                setDataForm(prev => ({ ...(prev || {}), [name]: checked }));
                return;
            }
        }

        // Validation du téléphone
        if (name === 'telephone') {
            const phoneRegex = /^\d{9}$/;
            SetIsValid((valid) => ({
                ...valid,
                isValidPhoneNumber: phoneRegex.test(value),
            }));
        }

        setDataForm({ ...dataForm, [name]: value });
    };

    // memoized map posteName -> array of etablissement codes (for rendering default checked state)
    const posteEtabMap = useMemo(() => {
        const map = {};
        const pairs = getPosteEtablissementPairs();
        pairs.forEach(p => {
            map[p.poste] = p.etablissements || [];
        });
        return map;
    }, [dataForm, selectedPostes]);

    const handleFileChange = (event, key) => {
        const file = event.target.files[0];
        
        if (file && file.size < maxFileSize) {
            setSelectedFiles((prevSelectedFiles) => ({
                ...prevSelectedFiles,
                [key]: file
            }));
        } else if (file) {
            alert(`Le fichier ${key} est trop volumineux (max 2Mo)`);
        }
    };

    // Ajouter un nouveau champ de fichier complémentaire
    const addAdditionalFile = () => {
        const newFile = {
            id: Date.now(),
            label: '',
            file: null
        };
        setAdditionalFiles([...additionalFiles, newFile]);
    };

    // Supprimer un fichier complémentaire
    const removeAdditionalFile = (id) => {
        setAdditionalFiles(additionalFiles.filter(file => file.id !== id));
    };

    // Gérer le changement de label du fichier complémentaire
    const handleAdditionalFileLabel = (id, label) => {
        setAdditionalFiles(additionalFiles.map(file => 
            file.id === id ? { ...file, label } : file
        ));
    };

    // Gérer le fichier complémentaire
    const handleAdditionalFileChange = (id, event) => {
        const file = event.target.files[0];
        if (file && file.size < maxFileSize) {
            setAdditionalFiles(additionalFiles.map(f => 
                f.id === id ? { ...f, file } : f
            ));
        } else if (file) {
            alert(`Le fichier est trop volumineux (max 2Mo)`);
        }
    };

    // Afficher/masquer un champ optionnel
    const toggleFieldVisibility = (fieldName) => {
        setVisibleFields(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    // Gestion des postes et établissements (édition) - adapté depuis RecrutementPer
    const handleEtablissementChange = (posteName, posteIndex, etsCode, checked) => {
        
        console.log(posteName, posteIndex, etsCode, checked);
        let resolvedPosteIndex = posteIndex;
        if ((resolvedPosteIndex === null || resolvedPosteIndex === undefined) && posteName) {
            for (let i = 1; i <= 6; i++) {
                if (dataForm?.[`poste${i}`] === posteName) { resolvedPosteIndex = i; break; }
            }
        }
        setDataForm(prevData => {
            const newData = { ...prevData };

            // Récupérer tous les établissements actuels pour ce poste (utilise poste_eta_ si présent,
            // sinon on considère la correspondance par index: etablissementN <-> posteN)
            const linkedKeys = Object.keys(newData).filter(key => {
                if (!key.startsWith('etablissement')) return false;
                const mapped = newData[`poste_eta_${key}`];
                if (mapped !== undefined && mapped !== null && mapped !== '') {
                    return Number(mapped) === Number(resolvedPosteIndex);
                }
                const m = key.match(/\d+/);
                return m ? Number(m[0]) === Number(resolvedPosteIndex) : false;
            });

            if (checked) {
                // En mode édition: remplacer la première entrée liée au poste
                if (editingPostesMode && linkedKeys.length > 0) {
                    const keyToUse = linkedKeys[0];
                    newData[keyToUse] = etsCode;
                    newData[`poste_eta_${keyToUse}`] = resolvedPosteIndex;
                } else if (editingPostesMode) {
                    // no linkedKeys found but in edit mode: try to replace etablissement by same numeric index (etablissement{posteIndex})
                    const fallbackKey = `etablissement${resolvedPosteIndex}`;
                    if (newData.hasOwnProperty(fallbackKey)) {
                        newData[fallbackKey] = etsCode;
                        newData[`poste_eta_${fallbackKey}`] = resolvedPosteIndex;
                    } else {
                        // fall back to adding a new entry (if space)
                        const existingIndices = Object.keys(newData)
                            .map(k => { const m = k.match(/^etablissement(\d+)$/); return m ? parseInt(m[1], 10) : null; })
                            .filter(n => !!n);
                        let idx = 1;
                        while (existingIndices.includes(idx) && idx <= 6) idx++;
                        if (idx <= 6) {
                            newData[`etablissement${idx}`] = etsCode;
                            newData[`poste_eta_etablissement${idx}`] = resolvedPosteIndex;
                            setEtablissementCounter(Math.max(etablissementCounter, idx));
                        } else {
                            alert("Nombre maximum d'établissements atteint (6). Remplacez un établissement existant.");
                        }
                    }
                } else {
                    // Sinon, ajouter une nouvelle entrée si il reste de la place (max 6)
                    // Calculer indices existants
                    const existingIndices = Object.keys(newData)
                        .map(k => { const m = k.match(/^etablissement(\d+)$/); return m ? parseInt(m[1], 10) : null; })
                        .filter(n => !!n);
                    let idx = 1;
                    while (existingIndices.includes(idx) && idx <= 6) idx++;
                    if (idx <= 6) {
                        newData[`etablissement${idx}`] = etsCode;
                        newData[`poste_eta_etablissement${idx}`] = resolvedPosteIndex;
                        setEtablissementCounter(Math.max(etablissementCounter, idx));
                    } else {
                        alert("Nombre maximum d'établissements atteint (6). Remplacez un établissement existant.");
                    }
                }
            } else {
                // Décocher: en mode édition, supprimer la première entrée liée (laisser autres intactes)
                if (editingPostesMode && linkedKeys.length > 0) {
                    const keyToClear = linkedKeys[0];
                    delete newData[keyToClear];
                    delete newData[`poste_eta_${keyToClear}`];
                } else if (editingPostesMode) {
                    // try to clear by numeric index fallback
                    const fallbackKey = `etablissement${resolvedPosteIndex}`;
                    if (newData.hasOwnProperty(fallbackKey)) {
                        delete newData[fallbackKey];
                        delete newData[`poste_eta_${fallbackKey}`];
                    }
                } else {
                    // Supprimer toute occurrence correspondant
                    Object.keys(newData).forEach(key => {
                        if (key.startsWith('etablissement') && newData[key] === etsCode && newData[`poste_eta_${key}`] === resolvedPosteIndex) {
                            delete newData[key];
                            delete newData[`poste_eta_${key}`];
                        }
                    });
                }
            }
            console.log("Updated dataForm after etablissement change:", newData);
            return newData;
        });
    };

    const isEtablissementSelected = (posteIndex, etsCode) => {
        // If any etablissement entry equals etsCode, try to infer its linked poste(s)
        const ecole = dataForm?.ecole_postulee;
        const etKeys = Object.keys(dataForm || {}).filter(k => k.startsWith('etablissement') && dataForm[k] === etsCode);
        if (etKeys.length === 0) return false;

        for (const key of etKeys) {
            const mapped = dataForm[`poste_eta_${key}`];
            if (mapped !== undefined && mapped !== null && mapped !== '') {
                if (Number(mapped) === Number(posteIndex)) return true;
            }
            // If no explicit mapping, use postesPer to see which postes accept this etablissement
            if (ecole && postesPer?.[ecole]) {
                const candidatePostes = Object.keys(postesPer[ecole]).filter(pName => {
                    const allowed = postesPer[ecole][pName] || [];
                    return allowed.includes(etsCode);
                });
                // find the poste name at posteIndex
                const posteNameAtIndex = dataForm?.[`poste${posteIndex}`];
                if (posteNameAtIndex && candidatePostes.includes(posteNameAtIndex)) return true;
            }
            // fallback positional match: etablissementN -> posteN
            const m = key.match(/\d+/);
            if (m && Number(m[0]) === Number(posteIndex)) return true;
        }
        return false;
    };

    const handlePosteSelection = (poste, checked) => {
        if (checked) {
            // Ajouter le poste à la liste
            // Trouver la première position libre entre 1 et 6 (éviter poste7)
            // Prefer using an existing poste slot that is present but null/empty in dataForm
            let newIndex = null;
            for (let i = 1; i <= 6; i++) {
                const key = `poste${i}`;
                if (dataForm && Object.prototype.hasOwnProperty.call(dataForm, key)) {
                    const val = dataForm[key];
                    if (val === null || val === undefined || val === '') {
                        newIndex = i;
                        break;
                    }
                }
            }
            if (!newIndex) {
                const existingIndices = new Set([
                    ...selectedPostes.map(p => p.index),
                    // n'inclure que les postes présents et non nuls dans dataForm
                    ...Object.keys(dataForm || {})
                        .filter(k => /^poste\d+$/.test(k) && dataForm[k])
                        .map(k => Number(k.match(/^poste(\d+)$/)[1]))
                ]);
                for (let i = 1; i <= 6; i++) {
                    if (!existingIndices.has(i)) { newIndex = i; break; }
                }
            }
            if (!newIndex) {
                alert('Vous ne pouvez pas sélectionner plus de 6 postes.');
                return;
            }
            // Debug log: nouveau poste sélectionné (index proposé)
            console.log('[DEBUG] handlePosteSelection - adding poste', { poste, newIndex, checked });
            // Add to selectedPostes and commit posteN into dataForm immediately so etablissement toggles map correctly
            setSelectedPostes(prev => [...prev, { poste, index: newIndex }]);
            setDataForm(prev => ({ ...(prev || {}), [`poste${newIndex}`]: poste }));
            console.log('[DEBUG] handlePosteSelection - wrote poste into dataForm', { key: `poste${newIndex}`, value: poste });
        } else {
            // Retirer le poste et tous ses établissements associés, puis compact et réindexer
            const updatedPostes = selectedPostes.filter(p => p.poste !== poste);
            const newDataForm = { ...dataForm };
            const posteToRemove = selectedPostes.find(p => p.poste === poste);

            // Mapping old poste index -> new poste index
            const oldToNewPosteIndex = {};
            updatedPostes.forEach((p, idx) => {
                oldToNewPosteIndex[p.index] = idx + 1;
            });

            // Supprimer l'entrée du poste supprimé
            if (posteToRemove) {
                delete newDataForm[`poste${posteToRemove.index}`];
            }

            // Collecter tous les établissements restants (exclure ceux liés au poste supprimé)
            const remainingEts = [];
            Object.keys(newDataForm).forEach(key => {
                if (key.startsWith('etablissement') && key.match(/\d+/)) {
                    const code = newDataForm[key];
                    const posteIdx = newDataForm[`poste_eta_${key}`];
                    if (posteIdx && posteToRemove && Number(posteIdx) === posteToRemove.index) {
                        // supprimer l'entrée liée au poste supprimé
                        delete newDataForm[key];
                        delete newDataForm[`poste_eta_${key}`];
                    } else if (code) {
                        remainingEts.push({ key, code, posteIdx: posteIdx ? Number(posteIdx) : null });
                        // supprimer maintenant; on les réinsérera compactés
                        delete newDataForm[key];
                        delete newDataForm[`poste_eta_${key}`];
                    }
                }
            });

            // Réinsérer les établissements de façon compacte (etablissement1..N)
            remainingEts.forEach((ets, idx) => {
                const newEtaKey = `etablissement${idx + 1}`;
                newDataForm[newEtaKey] = ets.code;
                const originalPosteIdx = ets.posteIdx;
                const mappedPosteIdx = originalPosteIdx ? (oldToNewPosteIndex[originalPosteIdx] || null) : null;
                if (mappedPosteIdx) {
                    newDataForm[`poste_eta_${newEtaKey}`] = mappedPosteIdx;
                }
            });

            // Mettre à jour le compteur global d'établissements
            setEtablissementCounter(remainingEts.length);

            // Réindexer et renuméroter les postes (poste1..)
            updatedPostes.forEach((p, idx) => {
                const newIdx = idx + 1;
                const oldIdx = p.index;
                if (newDataForm[`poste${oldIdx}`]) {
                    newDataForm[`poste${newIdx}`] = newDataForm[`poste${oldIdx}`];
                    delete newDataForm[`poste${oldIdx}`];
                }
                Object.keys(newDataForm).forEach(key => {
                    if (key.startsWith('poste_eta_') && newDataForm[key] === oldIdx) {
                        newDataForm[key] = newIdx;
                    }
                });
                p.index = newIdx;
            });

            setSelectedPostes(updatedPostes);
            setDataForm(newDataForm);
            console.log('[DEBUG] handlePosteSelection - removed poste', { poste, updatedPostes, etablissementCounter, newDataForm });
        }
    };

    const getPosteIndex = (poste) => {
        const found = selectedPostes.find(p => p.poste === poste);
        return found ? found.index : null;
    };

    const populateSelectedPostesFromDataForm = () => {
        const postes = [];
        let maxEtab = 0;
        Object.keys(dataForm || {}).forEach(key => {
            const m = key.match(/^poste(\d+)$/);
            if (m) {
                const idx = parseInt(m[1], 10);
                const posteName = dataForm[key];
                postes.push({ poste: posteName, index: idx });
            }
            const m2 = key.match(/^etablissement(\d+)$/);
            if (m2) {
                const idx2 = parseInt(m2[1], 10);
                if (idx2 > maxEtab) maxEtab = idx2;
            }
        });
        // trier par index
        postes.sort((a, b) => a.index - b.index);
        if (postes.length > 0) {
            setSelectedPostes(postes);
        }
        if (maxEtab > 0) setEtablissementCounter(maxEtab);
            if (postes.length > 0) setEditingPostesMode(true);
    };

    // Build pairs (poste, etablissement) using positional fallback and validate against config
    function getPosteEtablissementPairs() {
        // Return array of { poste, etablissements: [codes] }
        const pairs = [];
        const ecole = dataForm?.ecole_postulee;

        // collect all etablissement entries
        const etabs = Object.keys(dataForm || {}).filter(k => k.startsWith('etablissement') && dataForm[k]);

        for (let i = 1; i <= 6; i++) {
            const poste = dataForm?.[`poste${i}`];
            if (!poste) continue;

            const matchedEtabs = [];

            // 1) find etabs explicitly mapped via poste_eta_ keys
            for (const key of etabs) {
                const mapped = dataForm[`poste_eta_${key}`];
                if (mapped !== undefined && mapped !== null && mapped !== '') {
                    if (Number(mapped) === i) matchedEtabs.push(dataForm[key]);
                }
            }

            // positional fallback: etablissementN -> posteN (use if still empty)
            if (matchedEtabs.length === 0) {
                const posKey = `etablissement${i}`;
                if (dataForm?.[posKey]) matchedEtabs.push(dataForm[posKey]);
            }

            pairs.push({ poste, etablissements: matchedEtabs });
        }

        return pairs;
    };

    // Auto-populate postes when dataForm is first loaded (so checkboxes reflect existing choices)
    useEffect(() => {
        if (dataForm && Object.keys(dataForm).some(k => /^poste\d+$/.test(k)) && selectedPostes.length === 0) {
            populateSelectedPostesFromDataForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataForm]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        
        try {
            setSubmitting(true);
            setError(null);
            
            const formData = new FormData();
            
            // Liste des champs qui sont des fichiers
            const fileFields = [
                'cv', 'copie_carte_id', 'demande', 'lettre_motivation', 'copie_diplome',
                'communication', 'recommandation1', 'recommandation2', 'resume_these',
                'publication_scientifique', 'attestation_travail', 'justificatif1',
                'justificatif2', 'justificatif3', 'justificatif4', 'justificatif5',
                'justificatif6', 'diplome2', 'diplome3', 'diplome4', 'diplome5',
                'diplome6', 'justificatif7', 'justificatif8', 'communication2',
                'communication3', 'communication4', 'communication5', 'publication2',
                'publication3', 'publication4', 'publication5', 'publication6',
                'publication7', 'publication8', 'publication9', 'justificatif_enseignement'
            ];
            
            // Ajouter tous les champs du formulaire SAUF les fichiers
            for (let key in dataForm) {
                console.log(key, dataForm[key]);
                if (dataForm.hasOwnProperty(key) && 
                    dataForm[key] !== null && 
                    key !== 'uid' && 
                    key !== 'date_depot' && 
                    key !== 'date_update' &&
                    !fileFields.includes(key)) {
                    // Exclure les champs internes poste_eta_ utilisés pour le mapping local
                    if (key.startsWith('poste_eta_')) continue;
                    formData.append(key, dataForm[key]);
                }
            }
            // verifier si les autres postes ont leur etablissement associé
            const pairsEts = getPosteEtablissementPairs();
            const missingPairETs = pairsEts.some(pair => !pair.etablissements || pair.etablissements.length === 0);
            if (missingPairETs) {
                setError("Chaque poste doit être lié à au moins un établissement.");
                setSubmitting(false);
                return;
            }
            
            // Ajouter UNIQUEMENT les nouveaux fichiers uploadés
            for (let key in selectedFiles) {
                if (selectedFiles[key] && selectedFiles[key] instanceof File) {
                    formData.append(key, selectedFiles[key]);
                }
            }

            // Ajouter les fichiers complémentaires avec leurs labels
            additionalFiles.forEach((additionalFile, index) => {
                if (additionalFile.file && additionalFile.label) {
                    // Utiliser un nom de champ dynamique pour les fichiers complémentaires
                    formData.append(`document_complementaire_${index}`, additionalFile.file);
                    formData.append(`document_complementaire_${index}_label`, additionalFile.label);
                }
            });

            const response = await axios.post(
                `${API_URL}/recrutement/update`,
                formData,
                {
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                }
            );

            // Validation: chaque poste sélectionné doit avoir au moins un établissement associé
            const pairs = getPosteEtablissementPairs();
            const missingPair = pairs.some(pair => !pair.etablissements || pair.etablissements.length === 0);
            if (missingPair) {
                setError("Chaque poste doit être lié à au moins un établissement.");
                setSubmitting(false);
                return;
            }

            // Ensure all etablissements from pairs are present in the multipart formData as etablissementN
            // Collect existing etablissement values already in dataForm
            const existingEtabs = new Set();
            for (const key of Object.keys(dataForm || {})) {
                if (key.startsWith('etablissement') && dataForm[key]) existingEtabs.add(dataForm[key]);
            }

            // Determine used numeric indices
            const usedIndices = new Set();
            for (const key of Object.keys(dataForm || {})) {
                const m = key.match(/^etablissement(\d+)$/);
                if (m) usedIndices.add(Number(m[1]));
            }

            // Helper to find next free index 1..6
            const nextFreeIndex = () => {
                for (let i = 1; i <= 6; i++) if (!usedIndices.has(i)) return i;
                return null;
            };

            // Append missing etablissement entries to formData (do not overwrite existing)
            for (const pair of pairs) {
                for (const etsCode of pair.etablissements || []) {
                    if (existingEtabs.has(etsCode)) continue; // already present
                    const idx = nextFreeIndex();
                    if (!idx) {
                        setError('Impossible d\'ajouter les établissements : limite de 6 atteinte.');
                        setSubmitting(false);
                        return;
                    }
                    const etaKey = `etablissement${idx}`;
                    formData.append(etaKey, etsCode);
                    formData.append(`poste_eta_${etaKey}`, String(pairs.indexOf(pair) + 1));
                    usedIndices.add(idx);
                    existingEtabs.add(etsCode);
                }
            }

            // Append the poste/etablissement pairs as JSON for backend reference (optional)
            formData.append('poste_etablissement_pairs', JSON.stringify(pairs));

            if (response.status === 200) {
                console.log(response)
                setSuccess(true);
                setSubmitting(false);
                setMessage("Données mises à jour avec succès")
                // setTimeout(() => {
                //     navigate('/recrutement');
                // }, 2000);
            }
        } catch (err) {
            console.error('Erreur lors de la mise à jour:', err);
            setError("Une erreur s'est produite lors de la mise à jour");
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="container text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </Spinner>
                <p className="mt-3">Chargement des données...</p>
            </div>
        );
    }

    if (error && !dataForm) {
        return (
            <div className="container mt-5">
                <ErrorPage errorCode={statusError}/>
            </div>
        );
    }

    return (
        <div className="container mt-4 mb-2" id="updateRecrutement">
            <div className="update-recrutement-header">
                <h6>Mise à jour du dossier de recrutement</h6>
            </div>

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
                    Votre dossier a été mis à jour avec succès !
                </Alert>
            )}
            {
                success && (
                    <Alert2 message={message} />
                )
            }
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Form onSubmit={handleSubmit} className="update-form">
                {/* Informations personnelles */}
                <div className="form-section">
                    <h4>Informations personnelles</h4>
                    
                    <Input 
                        type='email' 
                        value={dataForm?.email || ''} 
                        label="Adresse mail" 
                        name='email' 
                        onChange={handleChange}
                        disabled
                    />

                    <Form.Group className="mb-3">
                        <div className='d-inline'>
                            <Form.Check
                                inline
                                type="radio"
                                label="Monsieur"
                                value='Monsieur'
                                checked={dataForm?.civilite === 'Monsieur'}
                                name="civilite"
                                onChange={handleChange}
                            />
                        </div>
                        <div className='d-inline'>
                            <Form.Check
                                inline
                                type="radio"
                                label="Madame"
                                value='Madame'
                                checked={dataForm?.civilite === 'Madame'}
                                name="civilite"
                                onChange={handleChange}
                            />
                        </div>
                    </Form.Group>

                    <div className='row'>
                        <Input 
                            className="col-md-6 col-sm-12" 
                            value={dataForm?.prenom || ''} 
                            type='text' 
                            label="Prénom (s)" 
                            name='prenom' 
                            onChange={handleChange} 
                        />
                        <Input 
                            className="col-md-6 col-sm-12" 
                            value={dataForm?.nom || ''} 
                            type='text' 
                            label="Nom" 
                            name='nom' 
                            onChange={handleChange}
                        />
                    </div>

                    <div className='row'>
                        <Input 
                            className="col-md-6 col-sm-12" 
                            value={dataForm?.date_naissance || ''} 
                            type='date' 
                            label="Date de Naissance" 
                            name='date_naissance' 
                            onChange={handleChange} 
                        />
                        <Input 
                            className="col-md-6 col-sm-12" 
                            value={dataForm?.lieu_naissance || ''} 
                            type='text' 
                            label="Lieu de naissance" 
                            name='lieu_naissance' 
                            onChange={handleChange} 
                        />
                    </div>

                    <Input 
                        type='number' 
                        value={dataForm?.telephone || ''} 
                        label="Téléphone" 
                        name='telephone' 
                        onChange={handleChange} 
                    />
                    {!valid.isValidPhoneNumber && (
                        <span style={{color:'red'}}>Format du numéro incorrect</span>
                    )}

                    <Input 
                        type='text' 
                        value={dataForm?.address || ''} 
                        label="Adresse" 
                        name='address' 
                        onChange={handleChange} 
                    />
                </div>

                {/* Informations professionnelles */}
                
                <div className="form-section">
                    {!dataForm.nom_projet && (
                        <div>
                    <h4>Informations professionnelles</h4>
                    
                    <Form.Group className="mb-3">
                        <Form.Label className='fw-medium'>Type de poste</Form.Label>
                        <Form.Select 
                            name='type_poste' 
                            value={dataForm?.type_poste || ''} 
                            onChange={handleChange}
                            disabled
                        >
                            <option value="">--- Sélectionner ---</option>
                            {type_postes.map((type, index) => (
                                <option key={index} value={type}>{type}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {dataForm?.type_poste === 'PER' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label className='fw-medium'>Domaine</Form.Label>
                                <Form.Select 
                                    disabled
                                    name='ecole_postulee' 
                                    value={dataForm?.ecole_postulee || ''} 
                                    onChange={handleChange}
                                >
                                    <option disabled value="">--- Sélectionner ---</option>
                                    {domaines.map((domaine, index) => (
                                        <option key={index} value={domaine}>{domaine}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className='fw-medium'>Poste / Spécialité</Form.Label>
                                {/* Si on a déjà des postes dans dataForm, permettre de les charger pour édition */}
                                {Object.keys(dataForm || {}).some(k => /^poste\d+$/.test(k)) ? (
                                    <div className="mb-2">
                                        {/* <Button size="sm" variant="outline-secondary" onClick={populateSelectedPostesFromDataForm}>Modifier postes existants</Button> */}
                                    </div>
                                ) : null}
                                {postesPer[dataForm?.ecole_postulee] ? 
                                <>
                                    <div className="border rounded p-1" style={{maxHeight: '600px', overflowY: 'auto'}}>
                                        {Object.entries(postesPer[dataForm?.ecole_postulee]).map(([poste, etablissements], index) => {
                                            const posteIndex = getPosteIndex(poste);
                                            const isSelected = posteIndex !== null;
                                            // disable poste checkbox only if that poste came from backend (original data)
                                            const isExistingPoste = backendPostes.includes(poste);
                                            
                                            return (
                                                <div key={index} className="mb-1 pb-2 border-bottom">
                                                    <div className="mb-0">
                                                        <Form.Check 
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            disabled={isExistingPoste}
                                                            label={`${poste}`}
                                                            onChange={(e) => handlePosteSelection(poste, e.target.checked)}
                                                            name={`poste_checkbox_${index}`}
                                                        />
                                                    </div>
                                                    {isSelected && (
                                                        <div className="">
                                                            <Form.Label className='fw-medium small'>Établissement(s) pour ce poste <span style={{color:'red'}}>*</span></Form.Label>
                                                            <div className=""></div>
                                                            {etablissements.map((etsCode, etaIdx) => {
                                                                const isBackendPoste = backendPostes.includes(poste);
                                                                // Pour les postes venant du backend, conserver l'ancien comportement (fallback positional ou mapping explicite)
                                                                // Pour les postes nouvellement ajoutés, n'accepter que les mappings explicites (poste_eta_) afin d'éviter les auto-cochages
                                                                let isChecked = false;
                                                                if (isBackendPoste) {
                                                                    isChecked = isEtablissementSelected(posteIndex, etsCode) || (posteEtabMap[poste] || []).includes(etsCode);
                                                                } else {
                                                                    // vérifier uniquement les mappings explicites dans dataForm: poste_eta_etablissementN ou poste_eta_etablissement{N}
                                                                    isChecked = Object.keys(dataForm || {}).some(key => {
                                                                        if (!key.startsWith('etablissement')) return false;
                                                                        if (dataForm[key] !== etsCode) return false;
                                                                        const mapped = dataForm[`poste_eta_${key}`];
                                                                        return mapped !== undefined && mapped !== null && String(mapped) === String(posteIndex);
                                                                    });
                                                                }
                                                                return (
                                                                    <div key={etaIdx} className="mb-1" style={{borderRadius: "0px 25px 25px 0px",
                                                                        boxShadow: "4px 2px 4px 2px rgba(26, 22, 22, 0.1)",
                                                                        padding: 2,
                                                                        border: "1px solid #99371f25",
                                                                        marginLeft: 9}}>
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            checked={isChecked}
                                                                            value={etsCode}
                                                                            label={libelleEts[etsCode]}
                                                                            onChange={(e) => handleEtablissementChange(poste, posteIndex, etsCode, e.target.checked)}
                                                                            name={`eta_check_${posteIndex}_${etaIdx}`}
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                                : <p className="text-muted">Veuillez d'abord sélectionner une filière</p> }
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className='fw-medium'>Grade CAMES</Form.Label>
                                <Form.Select 
                                    name='annee_experience' 
                                    value={dataForm?.annee_experience || ''} 
                                    onChange={handleChange}
                                >
                                    <option value="">--- Sélectionner ---</option>
                                    <option>Pas de grade</option>
                                    <option>Maitre assistant</option>
                                    <option>Maitre conférence</option>
                                    <option>Professeur titulaire</option>
                                </Form.Select>
                            </Form.Group>
                        </>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label className='fw-medium'>Diplôme</Form.Label>
                        <Form.Select 
                            name='diplome' 
                            value={dataForm?.diplome || ''} 
                            onChange={handleChange}
                        >
                            <option value="">--- Sélectionner ---</option>
                            {dataForm?.type_poste === 'PER' ? (
                                <>
                                    <option>Doctorat</option>
                                    <option>Thèse unique</option>
                                    <option>PHD ou equivalent</option>
                                    <option>Doctorat 3eme Cycle</option>
                                </>
                            ) : (
                                <>
                                    <option>Master</option>
                                    <option>Ingénieur</option>
                                    <option>Maitrise</option>
                                    <option>Licence</option>
                                    <option>DEUG / DUEL</option>
                                    <option>BAC + 2</option>
                                    <option>BTS</option>
                                    <option>DUT</option>
                                    <option>DTS</option>
                                    <option>BAC</option>
                                    <option>BFEM</option>
                                    <option>CFEE</option>
                                </>
                            )}
                        </Form.Select>
                    </Form.Group>
                    </div>
                    )}
                    
                        {/* fin de informations personnelles */}
                    {/* Informations projet (si présent) - déplacé hors de la section Documents */}
                    {dataForm?.nom_projet && (
                        <div className="form-section">
                            <h4>Informations projet</h4>
                            <Input
                                type='textarea'
                                value={dataForm?.nom_projet || ''}
                                label="Nom du projet"
                                name='nom_projet'
                                onChange={handleChange}
                            />
                            <Input
                                type='textarea'
                                value={dataForm?.objectif_projet || ''}
                                label="Objectif du projet"
                                name='objectif_projet'
                                onChange={handleChange}
                            />
                            <Input
                                type='textarea'
                                value={dataForm?.contexte_projet || ''}
                                label="Contexte du projet"
                                name='contexte_projet'
                                onChange={handleChange}
                            />
                            <Input
                                type='textarea'
                                value={dataForm?.acteur_responsabilite_projet || ''}
                                label="Acteur / Responsabilité du projet"
                                name='acteur_responsabilite_projet'
                                onChange={handleChange}
                            />
                            <Input
                                type='textarea'
                                value={dataForm?.partenariat_projet || ''}
                                label="Partenariat projet"
                                name='partenariat_projet'
                                onChange={handleChange}
                            />
                            <Input
                                type='textarea'
                                value={dataForm?.attente_projet || ''}
                                label="Attente projet"
                                name='attente_projet'
                                onChange={handleChange}
                            />
                            {/* <Input
                                type='text'
                                value={dataForm?.expertise_projet || ''}
                                label="Expertise projet"
                                name='expertise_projet'
                                onChange={handleChange}
                            /> */}
                        </div>
                    )}
                </div>

                {/* Documents */}
                <div className="form-section">
                    <h4>Documents</h4>
                    <p className="text-muted">
                        Vous pouvez télécharger de nouveaux fichiers pour remplacer les anciens. 
                        Si vous ne sélectionnez pas de nouveau fichier, l'ancien sera conservé.
                    </p>

                    {dataForm?.nom_projet ? (
                        // Mode projet : n'afficher que la lettre de soutien et les champs projet modifiables
                        <>
                            <InputF
                                className="mb-2 mt-2"
                                label="Lettre de Soutien *"
                                type='file'
                                name='resume_these'
                                onChange={(e) => handleFileChange(e, "resume_these")}
                            />
                            {dataForm?.resume_these && (
                                <small className="text-success">Ancien fichier: {dataForm.resume_these.split('/').pop()}</small>
                            )}
                            <InputF 
                                className="mb-2" 
                                label="CV *" 
                                type='file' 
                                name='cv' 
                                onChange={(e) => handleFileChange(e, "cv")} 
                            />
                            {dataForm?.cv && (
                                <small className="text-success">Ancien fichier: {dataForm.cv.split('/').pop()}</small>
                            )}

                            <InputF 
                                className="mb-2 mt-2" 
                                label="Projet *" 
                                type='file' 
                                name='demande' 
                                onChange={(e) => handleFileChange(e, "demande")} 
                            />
                            {dataForm?.demande && (
                                <small className="text-success">Ancien fichier: {dataForm.demande.split('/').pop()}</small>
                            )}
                            
                        </>
                    ) : (
                        // mode normal : rendu complet existant
                        <>
                            <InputF 
                                className="mb-2" 
                                label="Copie carte d'identité" 
                                type='file' 
                                name='copie_carte_id' 
                                onChange={(e) => handleFileChange(e, "copie_carte_id")} 
                            />
                            {dataForm?.copie_carte_id && (
                                <small className="text-success">Ancien fichier: {dataForm.copie_carte_id.split('/').pop()}</small>
                            )}

                            <InputF 
                                className="mb-2 mt-2" 
                                label="CV" 
                                type='file' 
                                name='cv' 
                                onChange={(e) => handleFileChange(e, "cv")} 
                            />
                            {dataForm?.cv && (
                                <small className="text-success">Ancien fichier: {dataForm.cv.split('/').pop()}</small>
                            )}

                            <InputF 
                                className="mb-2 mt-2" 
                                label="Demande" 
                                type='file' 
                                name='demande' 
                                onChange={(e) => handleFileChange(e, "demande")} 
                            />
                            {dataForm?.demande && (
                                <small className="text-success">Ancien fichier: {dataForm.demande.split('/').pop()}</small>
                            )}

                            <InputF 
                                className="mb-2 mt-2" 
                                label="Lettre de motivation" 
                                type='file' 
                                name='lettre_motivation' 
                                onChange={(e) => handleFileChange(e, "lettre_motivation")} 
                            />
                            {dataForm?.lettre_motivation && (
                                <small className="text-success">Ancien fichier: {dataForm.lettre_motivation.split('/').pop()}</small>
                            )}

                            <InputF 
                                className="mb-2 mt-2" 
                                label="Diplome" 
                                type='file' 
                                name='copie_diplome' 
                                onChange={(e) => handleFileChange(e, "copie_diplome")} 
                            />
                            {dataForm?.copie_diplome && (
                                <small className="text-success">Ancien fichier: {dataForm.copie_diplome.split('/').pop()}</small>
                            )}

                            {dataForm?.type_poste !== 'PATS' && (
                            <InputF 
                                className="mb-2 mt-2" 
                                label="Diplôme de BAC" 
                                type='file' 
                                name='diplome2' 
                                onChange={(e) => handleFileChange(e, "diplome2")} 
                            /> )}
                            {dataForm?.diplome2 && (
                                <small className="text-success">Ancien fichier: {dataForm.diplome2.split('/').pop()}</small>
                            )}

                            {visibleFields.diplome3 && (
                                <>
                                    <InputF 
                                        className="mb-2 mt-2" 
                                        label="Autre diplôme 1" 
                                        type='file' 
                                        name='diplome3' 
                                        onChange={(e) => handleFileChange(e, "diplome3")} 
                                    />
                                    {dataForm?.diplome3 && (
                                        <small className="text-success">Ancien fichier: {dataForm.diplome3.split('/').pop()}</small>
                                    )}
                                </>
                            )}

                            {visibleFields.diplome4 && (
                                <>
                                    <InputF 
                                        className="mb-2 mt-2" 
                                        label="Autre diplôme 2" 
                                        type='file' 
                                        name='diplome4' 
                                        onChange={(e) => handleFileChange(e, "diplome4")} 
                                    />
                                    {dataForm?.diplome4 && (
                                        <small className="text-success">Ancien fichier: {dataForm.diplome4.split('/').pop()}</small>
                                    )}
                                </>
                            )}

                            {visibleFields.diplome5 && (
                                <>
                                    <InputF 
                                        className="mb-2 mt-2" 
                                        label="Autre diplôme 3" 
                                        type='file' 
                                        name='diplome5' 
                                        onChange={(e) => handleFileChange(e, "diplome5")} 
                                    />
                                    {dataForm?.diplome5 && (
                                        <small className="text-success">Ancien fichier: {dataForm.diplome5.split('/').pop()}</small>
                                    )}
                                </>
                            )}

                            {visibleFields.diplome6 && (
                                <>
                                    <InputF 
                                        className="mb-2 mt-2" 
                                        label="Autre diplôme 4" 
                                        type='file' 
                                        name='diplome6' 
                                        onChange={(e) => handleFileChange(e, "diplome6")} 
                                    />
                                    {dataForm?.diplome6 && (
                                        <small className="text-success">Ancien fichier: {dataForm.diplome6.split('/').pop()}</small>
                                    )}
                                </>
                            )}

                            {(!visibleFields.diplome3 || !visibleFields.diplome4 || !visibleFields.diplome5 || !visibleFields.diplome6) && (
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    className="mt-2"
                                    onClick={() => {
                                        if (!visibleFields.diplome3) toggleFieldVisibility('diplome3');
                                        else if (!visibleFields.diplome4) toggleFieldVisibility('diplome4');
                                        else if (!visibleFields.diplome5) toggleFieldVisibility('diplome5');
                                        else if (!visibleFields.diplome6) toggleFieldVisibility('diplome6');
                                    }}
                                >
                                    + Ajouter un autre diplôme
                                </Button>
                            )}

                            {dataForm?.type_poste === 'PER' && (
                                <>
                                    <InputF 
                                        className="mb-2 mt-2" 
                                        label="Lettre de recommandation 1" 
                                        type='file' 
                                        name='recommandation1' 
                                        onChange={(e) => handleFileChange(e, "recommandation1")} 
                                    />
                                    {dataForm?.recommandation1 && (
                                        <small className="text-success">Ancien fichier: {dataForm.recommandation1.split('/').pop()}</small>
                                    )}

                                    <InputF 
                                        className="mb-2 mt-2" 
                                        label="Lettre de recommandation 2" 
                                        type='file' 
                                        name='recommandation2' 
                                        onChange={(e) => handleFileChange(e, "recommandation2")} 
                                    />
                                    {dataForm?.recommandation2 && (
                                        <small className="text-success">Ancien fichier: {dataForm.recommandation2.split('/').pop()}</small>
                                    )}

                                    <InputF 
                                        className="mb-2 mt-2" 
                                        label="Résumé de la thèse" 
                                        type='file' 
                                        name='resume_these' 
                                        onChange={(e) => handleFileChange(e, "resume_these")} 
                                    />
                                    {dataForm?.resume_these && (
                                        <small className="text-success">Ancien fichier: {dataForm.resume_these.split('/').pop()}</small>
                                    )}

                                    <h5 className="mt-3 mb-2">Publications scientifiques</h5>
                                    <InputF 
                                        className="mb-2" 
                                        label="Publication scientifique" 
                                        type='file' 
                                        name='publication_scientifique' 
                                        onChange={(e) => handleFileChange(e, "publication_scientifique")} 
                                    />
                                    {dataForm?.publication_scientifique && (
                                        <small className="text-success">Ancien fichier: {dataForm.publication_scientifique.split('/').pop()}</small>
                                    )}

                                    {[2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                                        visibleFields[`publication${num}`] && (
                                            <div key={`publication${num}`}>
                                                <InputF 
                                                    className="mb-2 mt-2" 
                                                    label={`Publication scientifique ${num}`}
                                                    type='file' 
                                                    name={`publication${num}`}
                                                    onChange={(e) => handleFileChange(e, `publication${num}`)} 
                                                />
                                                {dataForm?.[`publication${num}`] && (
                                                    <small className="text-success">Ancien fichier: {dataForm[`publication${num}`].split('/').pop()}</small>
                                                )}
                                            </div>
                                        )
                                    ))}

                                    {[2, 3, 4, 5, 6, 7, 8, 9].some(num => !visibleFields[`publication${num}`]) && (
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            className="mt-2"
                                            onClick={() => {
                                                const nextNum = [2, 3, 4, 5, 6, 7, 8, 9, 10].find(num => !visibleFields[`publication${num}`]);
                                                if (nextNum) toggleFieldVisibility(`publication${nextNum}`);
                                            }}
                                        >
                                            + Ajouter une publication scientifique
                                        </Button>
                                    )}

                                    <h5 className="mt-3 mb-2">Communications</h5>
                                    <InputF 
                                        className="mb-2" 
                                        label="Communication" 
                                        type='file' 
                                        name='communication' 
                                        onChange={(e) => handleFileChange(e, "communication")} 
                                    />
                                    {dataForm?.communication && (
                                        <small className="text-success">Ancien fichier: {dataForm.communication.split('/').pop()}</small>
                                    )}

                                    {[2, 3, 4, 5].map((num) => (
                                        visibleFields[`communication${num}`] && (
                                            <div key={`communication${num}`}>
                                                <InputF 
                                                    className="mb-2 mt-2" 
                                                    label={`Communication ${num}`}
                                                    type='file' 
                                                    name={`communication${num}`}
                                                    onChange={(e) => handleFileChange(e, `communication${num}`)} 
                                                />
                                                {dataForm?.[`communication${num}`] && (
                                                    <small className="text-success">Ancien fichier: {dataForm[`communication${num}`].split('/').pop()}</small>
                                                )}
                                            </div>
                                        )
                                    ))}

                                    {[2, 3, 4, 5].some(num => !visibleFields[`communication${num}`]) && (
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            className="mt-2"
                                            onClick={() => {
                                                const nextNum = [2, 3, 4, 5].find(num => !visibleFields[`communication${num}`]);
                                                if (nextNum) toggleFieldVisibility(`communication${nextNum}`);
                                            }}
                                        >
                                            + Ajouter une communication
                                        </Button>
                                    )}
                                </>
                            )}

                            <h5 className="mt-3 mb-2">Justificatifs</h5>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                visibleFields[`justificatif${num}`] && (
                                    <div key={`justificatif${num}`}>
                                        <InputF 
                                            className="mb-2 mt-2" 
                                            label={`Justificatif ${num}`}
                                            type='file' 
                                            name={`justificatif${num}`}
                                            onChange={(e) => handleFileChange(e, `justificatif${num}`)} 
                                        />
                                        {dataForm?.[`justificatif${num}`] && (
                                            <small className="text-success">Ancien fichier: {dataForm[`justificatif${num}`].split('/').pop()}</small>
                                        )}
                                    </div>
                                )
                            ))}

                            {[1, 2, 3, 4, 5, 6, 7, 8].some(num => !visibleFields[`justificatif${num}`]) && (
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    className="mt-2"
                                    onClick={() => {
                                        const nextNum = [1, 2, 3, 4, 5, 6, 7, 8].find(num => !visibleFields[`justificatif${num}`]);
                                        if (nextNum) toggleFieldVisibility(`justificatif${nextNum}`);
                                    }}
                                >
                                    + Ajouter un justificatif
                                </Button>
                            )}
                        </>
                    )}
                </div>

                {/* Boutons d'action */}
                <div className="form-actions mt-4">
                    <Button 
                        variant="secondary" 
                        onClick={() => navigate('/recrutement')}
                        disabled={submitting}
                    >
                        Annuler
                    </Button>
                    <Button 
                        variant="primary" 
                        type="submit"
                        disabled={submitting}
                        className="ms-3"
                    >
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Mise à jour en cours...
                            </>
                        ) : (
                            'Enregistrer les modifications'
                        )}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default UpdateRecrutement;
